import { EUROPACE_COMPLETENESS_STATUS, EUROPACE_FEASIBILITY_STATUS, EUROPACE_INSURED_RISKS } from 'cpool-constants';
import { z } from 'zod';

import { schemaBonitaetsRechnung } from './BonitaetsRechnung.schema';
import { schemaCompletenessMessage } from './CompletenessMessages.schema';
import { schemaId, schemaInteger } from '../../../utils/schema.utils';

// https://docs.api.europace.de/privatkredit/angebote/kex-angebote-api/#angebot

/*
    {
        anpassungen: Anpassungen
        gesamtkonditionen: AngebotGesamtkonditionen
        id: String!
        machbarkeit: Machbarkeit
        ratenkredit: AngebotRatenkredit
        ratenschutz: Ratenschutz
        sofortkredit: Boolean
        vollstaendigkeit: Vollstaendigkeit
        vorhersage: Vorhersage
        bonitaetsrechnung: Bonitaetsrechnung
    }
 */

// unfortunately we have to use passthrough() for all z.object because they could change at any time without notice
// also almost every key is optional because they are not documented as required, only a few are documented with an "!" with could indicate that they are required
export const schemaEuropaceOffer = z
    .object({
        anpassungen: z
            .object({
                angepasst: z.boolean().optional(),
            })
            .passthrough()
            .optional(),
        bonitaetsrechnung: schemaBonitaetsRechnung.optional(),
        gesamtkonditionen: z
            .object({
                effektivzins: z.number().optional(),
                gesamtkreditbetrag: z.number().optional(),
                konditionsspanne: z
                    .object({
                        maximum: z
                            .object({
                                effektivzins: z.number().optional(),
                                gesamtkreditbetrag: z.number().optional(),
                                rateMonatlich: z.number().optional(),
                                sollzins: z.number().optional(),
                            })
                            .optional(),
                        minimum: z
                            .object({
                                effektivzins: z.number().optional(),
                                gesamtkreditbetrag: z.number().optional(),
                                rateMonatlich: z.number().optional(),
                                sollzins: z.number().optional(),
                            })
                            .optional(),
                    })
                    .optional(),
                laufzeitInMonaten: schemaInteger.optional(),
                nettokreditbetrag: z.number().optional(),
                rateMonatlich: z.number().optional(),
                sollzins: z.number().optional(),
            })
            .passthrough()
            .optional(),
        // this is the id we use to identify the offer in the backend
        id: schemaId,
        machbarkeit: z
            .object({
                messages: z
                    .array(
                        z.object({
                            text: z.string().optional(),
                        }),
                    )
                    .optional(),
                status: z.nativeEnum(EUROPACE_FEASIBILITY_STATUS),
            })
            .passthrough()
            .optional(),
        // this is the europace id for the offer
        offerId: z.string(),
        ratenkredit: z
            .object({
                produktanbieter: z
                    .object({
                        anschrift: z
                            .object({
                                hausnummer: z.string().optional(),
                                ort: z.string().optional(),
                                plz: z.string().optional(),
                                strasse: z.string().optional(),
                            })
                            .passthrough()
                            .optional(),
                        logo: z
                            .object({
                                svg: z.string().optional(), // The field svg contains the URL of the svg and not the content. https://docs.api.europace.de/privatkredit/angebote/kex-angebote-api/#logo
                            })
                            .passthrough()
                            .optional(),
                        name: z.string().optional(),
                    })
                    .passthrough()
                    .optional(),
                produktbezeichnung: z.string().optional(),
                schlussrate: z.number().optional(),
            })
            .passthrough()
            .optional(),
        ratenschutz: z
            .object({
                praemieMonatlich: z.number().optional(),
                versicherteRisikenAntragsteller1: z.array(z.nativeEnum(EUROPACE_INSURED_RISKS)),
                versicherteRisikenAntragsteller2: z.optional(z.array(z.nativeEnum(EUROPACE_INSURED_RISKS))),
            })
            .optional(),
        sofortkredit: z.boolean(),
        vollstaendigkeit: z
            .object({
                messages: z.array(schemaCompletenessMessage.passthrough()).optional(),
                status: z.nativeEnum(EUROPACE_COMPLETENESS_STATUS).optional(),
            })
            .passthrough()
            .optional(),
        vorhersage: z
            .object({
                machbarkeit: z
                    .object({
                        score: z.number().optional(),
                    })
                    .optional(),
            })
            .optional(),
    })
    .passthrough();

export type SchemaEuropaceOffer = z.infer<typeof schemaEuropaceOffer>;
